import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Base API service providing common functionality for all API calls
 * Handles error handling, loading states, and request/response transformation
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Generic GET request with error handling
   */
  get<T>(endpoint: string): Observable<T> {
    this.setLoading(true);
    return this.http.get<T>(`${environment.apiBaseUrl}${endpoint}`).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic POST request with error handling
   */
  post<T>(endpoint: string, body: any): Observable<T> {
    this.setLoading(true);
    return this.http.post<T>(`${environment.apiBaseUrl}${endpoint}`, body).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic PUT request with error handling
   */
  put<T>(endpoint: string, body: any): Observable<T> {
    this.setLoading(true);
    return this.http.put<T>(`${environment.apiBaseUrl}${endpoint}`, body).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic DELETE request with error handling
   */
  delete<T>(endpoint: string): Observable<T> {
    this.setLoading(true);
    return this.http.delete<T>(`${environment.apiBaseUrl}${endpoint}`).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Handle HTTP errors and return user-friendly messages
   */

  private handleError(error: HttpErrorResponse): Observable<never> {

    let backendMessage: string | undefined;

    // 1. Extract backend message safely (razni formati)
    if (error.error) {
      if (typeof error.error === 'string') {
        try {
          const parsed = JSON.parse(error.error);
          backendMessage = parsed?.message || parsed?.title;
        } catch {
          backendMessage = error.error;
        }
      } else {
        backendMessage =
          error.error?.message ||
          error.error?.title ||
          error.error?.detail;
      }
    }

    const fallbackMessage = `Server returned code ${error.status}`;

    let errorMessage = backendMessage || fallbackMessage;

    // 2. HTTP status mapping (bez gubitka backend poruke)
    switch (error.status) {

      case 0:
        errorMessage = 'No connection to server';
        break;

      case 400:
        errorMessage = backendMessage || 'Bad request';
        break;

      case 401:
        errorMessage = backendMessage || 'Unauthorized';
        break;

      case 403:
        errorMessage = backendMessage || 'Forbidden';
        break;

      case 404:
        errorMessage = backendMessage || 'Not found';
        break;

      case 409:
        errorMessage = backendMessage || 'Conflict';
        break;

      case 422:
        errorMessage = backendMessage || 'Validation error';
        break;

      case 500:
        errorMessage = backendMessage || 'Server error';
        break;

      case 503:
        errorMessage = backendMessage || 'Service unavailable';
        break;

      default:
        errorMessage = backendMessage || fallbackMessage;
        break;
    }

    console.error('API Error FULL:', error);
    console.error('Extracted message:', errorMessage);

    this.setLoading(false);

    return throwError(() => new Error(errorMessage));
  }

  // private handleError(error: HttpErrorResponse): Observable<never> {
  //   let errorMessage = 'An unknown error occurred';
  //
  //   if (error.error instanceof ErrorEvent) {
  //     // Client-side error
  //     errorMessage = `Error: ${error.error.message}`;
  //   } else {
  //     // Server-side error
  //     switch (error.status) {
  //       case 400:
  //         errorMessage = 'Bad request. Please check your input.';
  //         break;
  //       case 401:
  //         errorMessage = 'Unauthorized. Please log in again.';
  //         break;
  //       case 403:
  //         errorMessage = 'Forbidden. You do not have permission to access this resource.';
  //         break;
  //       case 404:
  //         errorMessage = 'Resource not found.';
  //         break;
  //       case 500:
  //         errorMessage = 'Server error. Please try again later.';
  //         break;
  //       case 503:
  //         errorMessage = 'Service unavailable. Please try again later.';
  //         break;
  //       default:
  //         errorMessage = error.error?.message || `Server returned code ${error.status}`;
  //     }
  //   }
  //
  //   console.error('API Error:', error);
  //   this.setLoading(false);
  //   return throwError(() => new Error(errorMessage));
  // }

  /**
   * Set loading state
   */
  private setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  /**
   * Get current loading state
   */
  public get isLoading(): boolean {
    return this.loadingSubject.value;
  }
}
